import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useRequestCodeMutation, useVerifyCodeMutation } from '../../../api/subscribeApi';
import getSubdomain from '../../../utils/getSubdomain';
import { errMsg } from '../../../utils/errMsg';
import buttons from '../../../styles/shared/buttons.module.css';
import card from '../../../styles/shared/publicCard.module.css';
import styles from './SubscriberLoginPage.module.css';

const STEP = {
  EMAIL: 'email',
  CODE: 'code',
};

export default function SubscriberLoginPage() {
  const subdomainSlug = getSubdomain();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.EMAIL);
  const [orgSlugInput, setOrgSlugInput] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [subscriberId, setSubscriberId] = useState(null);

  const organizationSlug = subdomainSlug || orgSlugInput.trim();
  const myGroupsPath = subdomainSlug ? '/my-groups' : `/o/${organizationSlug}/my-groups`;

  const [requestCode, requestState] = useRequestCodeMutation();
  const [verifyCode, verifyState] = useVerifyCodeMutation();

  function handleRequestCode(e) {
    e.preventDefault();
    if (!organizationSlug) return;
    requestCode({ organizationSlug, email })
      .unwrap()
      .then((res) => {
        setSubscriberId(res.subscriber_id);
        if (res.dev_code) setCode(String(res.dev_code));
        setStep(STEP.CODE);
      })
      .catch(() => {});
  }

  function handleVerify(e) {
    e.preventDefault();
    verifyCode({ organizationSlug, subscriberId, code })
      .unwrap()
      .then(() => navigate(myGroupsPath, { replace: true }))
      .catch(() => {});
  }

  return (
    <div className={card.wrapper}>
      <Card title="Log in" className={card.card}>
        {step === STEP.EMAIL && (
          <form onSubmit={handleRequestCode} className={styles.panel}>
            <p className={styles.panelHint}>
              Enter your email to see the groups you&apos;re subscribed to.
            </p>
            {!subdomainSlug && (
              <InputText
                className={styles.panelInput}
                placeholder="organization-slug"
                aria-label="Organization slug"
                value={orgSlugInput}
                onChange={(e) => setOrgSlugInput(e.target.value)}
                required
              />
            )}
            <InputText
              type="email"
              className={styles.panelInput}
              placeholder="you@campus.edu"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button
              type="submit"
              label={requestState.isLoading ? 'Sending…' : 'Send code'}
              loading={requestState.isLoading}
              disabled={!organizationSlug || !email}
              className={buttons.accentBtn}
            />
            {requestState.isError && (
              <div className={styles.errorText}>
                {errMsg(
                  requestState.error,
                  'Could not send a code. Is your email from this organization?',
                )}
              </div>
            )}
          </form>
        )}

        {step === STEP.CODE && (
          <form onSubmit={handleVerify} className={styles.panel}>
            <p className={styles.panelHint}>
              Enter the 6-digit code we emailed to {email}.
            </p>
            <InputText
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              className={styles.panelInput}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <Button
              type="submit"
              label={verifyState.isLoading ? 'Verifying…' : 'Log in'}
              loading={verifyState.isLoading}
              className={buttons.accentBtn}
            />
            {verifyState.isError && (
              <div className={styles.errorText}>
                {errMsg(verifyState.error, 'That code is invalid or expired.')}
              </div>
            )}
          </form>
        )}
      </Card>
    </div>
  );
}
